import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SbDeviceOptionsComponent } from 'src/app/components/lists/sb-device-list/sb-device-options/sb-device-options.component';
import { API, DeviceFullDTO, FixedAspectRatioImageDTO, SingleFileUpload } from 'src/app/services/api/api';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@ngx-seatback/utils';
import { DeviceFullDTOAndTags } from '../../../models/taggable.model';
import { TagService } from '../../../services/tag.service';
import { Clipping } from '../../../components/image/model';
import { ImageUploadModel } from '../../../services/gui/image/model';
import { ImageUploadService } from '../../../services/gui/image/image-upload.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-device-details',
  templateUrl: './device-details.component.html',
  styleUrls: ['./device-details.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class DeviceDetailsComponent implements OnInit {
  deviceId: number;
  device: DeviceFullDTOAndTags;
  status: DeviceDetailsOnlineStatus[];
  loading: boolean;

  headerUrl: string = './assets/img/companydetails-bg.jpg';

  logoImageUpload: ImageUploadModel;
  headerImageUpload: ImageUploadModel;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    private modal: ModalService,
    public util: UtilService,
    public errorHandler: ErrorService,
    public translate: TranslateService,
    private navService: NavigationService,
    private imageUploadService: ImageUploadService,
    public tagService: TagService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.headerImageUpload = new ImageUploadModel();

    this.deviceId = undefined;
    this.device = undefined;
    this.status = [];
    this.loading = true;

    this.headerUrl = './assets/img/companydetails-bg.jpg';
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.deviceId = +params.deviceID;
      this.refreshFromServer();
    });
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .ss_nm_Device_retrieveFullById(this.deviceId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (e: DeviceFullDTO) => {
          this.device = e;
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            const devices = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Device, [this.device]);
            this.device = devices[0];
          }

          this.headerUrl = this.device?.headerImage
            ? this.util.appendQueryParamToUrl(this.device.headerImage.url as unknown as string, '')
            : './assets/img/devicedetails-bg.jpg';
          this.status = [];

          this.status.push({ type: 'paired', value: this.device.paired });
          this.status.push({ type: 'productive', value: this.device.productive });
          this.status.push({ type: 'noSim', value: this.device.offline });
          this.status.push({ type: 'external', value: this.device.external });
          this.status.push({ type: 'disabled', value: this.device.disabled });
        },
        error: error => {
          if (error.status && (+error.status === 403 || +error.status === 404)) {
            this.navService.navigateToPoint(this.util.RoutingID.devices);
          } else {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  showOptionsModal() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: SbDeviceOptionsComponent, data: { device: this.device } },
        title: this.translate.instant('lang.globals.device') + ' ' + this.device.id,
        icon: 'settings',
        modalOptions: { size: 'lg' },
      })
      .then(() => {
        this.refreshFromServer();
      })
      .catch(() => {
        this.refreshFromServer();
      });
  }

  pickHeader() {
    this.headerImageUpload?.destroySubscriptions();
    this.api
      .ss_nm_Device_retrieveById(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (device: DeviceFullDTO) => {
          this.headerImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.header'));
          this.headerImageUpload.imageTransfer.image = device?.headerImage;
          this.headerImageUpload.subClippingChange = this.headerImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.headerClippingChange(res);
          });
          this.headerImageUpload.subFileChange = this.headerImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.headerFileChange(file);
          });
          this.headerImageUpload.subDeletePress = this.headerImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.headerDeletePress();
          });
          this.headerImageUpload.subModalClosed = this.headerImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.headerUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.headerImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.headerImageUpload.imageTransfer.image;
      this.headerImageUpload.imageTransfer.image = null;
      this.headerImageUpload.imageTransfer.uploading = true;
      this.api
        .ss_nm_Device_clipHeaderImage(this.deviceId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.headerImageUpload.imageTransfer.image = image;
            this.headerImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  headerFileChange(file: SingleFileUpload) {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Device_uploadHeaderImage(this.deviceId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.headerImageUpload.imageTransfer.image = res;
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerDeletePress() {
    this.headerImageUpload.imageTransfer.image = null;
    this.headerImageUpload.imageTransfer.uploading = true;
    this.api
      .ss_nm_Device_deleteHeaderImage(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.headerImageUpload.imageTransfer.image = null;
          }
          this.headerImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  headerUploadModalClosed() {
    this.headerImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }
}

export interface DeviceDetailsOnlineStatus {
  type: string;
  value: boolean;
}
